































































































import { Component, Vue, Prop, Inject, Watch } from 'vue-property-decorator';
import { SmartQuery } from 'vue-apollo-decorator';
import { UPDATE_CUSTOMER } from '@/queries/Customer';
import Order from '../Order/Order.vue';
import { GQL_customerDeliveryLocations_customer_deliveryLocations } from '@/queries/__generated__/customerDeliveryLocations';
import { GQL_updateCustomer_updateCustomer } from '@/queries/__generated__/updateCustomer';
import { ALL_COUNTRIES } from '@/queries/Country';
import { GQL_allCountries_allCountries } from '@/queries/__generated__/allCountries';
import Bugsnag from '@bugsnag/js';
import { cloneDeep } from 'lodash';
import gql from 'graphql-tag';
import { postCityForZip } from '@/utils/postal-code';

const CUSTOMER_DELIVERY_LOCATIONS = gql`
  query customerDeliveryLocations($id: ID!) {
    customer(id: $id) {
      id
      deliveryLocations {
        id
        address
        additionalAddress
        city
        zip
        country {
          code
          name
        }
      }
    }
  },
`;

@Component
export default class DocumentDelivery extends Vue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop(Object) public delivery!: any;
  @Prop(String) public customerId!: string;

  @SmartQuery({
    query: ALL_COUNTRIES,
    skip() { return !this.editable },
  }) public allCountries!: GQL_allCountries_allCountries;

  @SmartQuery({
    query: CUSTOMER_DELIVERY_LOCATIONS,
    variables() {
      return {
        id: this.customerId,
      };
    },
    skip() {
      return !this.editable || !this.customerId;
    },
    update: data => data.customer?.deliveryLocations || [],
  }) public deliveryLocations: GQL_customerDeliveryLocations_customer_deliveryLocations[] = [];

  @Watch('customerId')
  public unsettDeliveryLocations() {
    this.deliveryLocations = [];
  }

  @Watch('deliveryLocations', {deep: true})
  private isPotentiallyNewDeliveryAddress(): boolean {
    if (!this.editable || !this.customerId) return false;

    const deliveriesToCompare = this.deliveryLocations.map(deliveryLocation => {
      return {
        countryCode: deliveryLocation.country.code,
        address: deliveryLocation.address,
        // location.additionalAddress is always a string.
        // deliveryLocation.additionalAddress is string or null
        // we must always compare them as strings
        additionalAddress: deliveryLocation.additionalAddress || '',
        zip: deliveryLocation.zip,
        city: deliveryLocation.city,
      };
    });

    return deliveriesToCompare.every(location => {
      return (
        location.countryCode !== this.delivery.country.code ||
        location.address !== this.delivery.streetName ||
        location.additionalAddress !== (this.delivery.additionalStreetName || '') ||
        location.zip !== this.delivery.zip ||
        location.city !== this.delivery.city
      );
    });
  }

  @Inject() public documentComponent!: Order;
  @Inject() public documentType!: 'order' | 'invoice';

  get editable() {
    return this.documentType === 'order' && this.documentComponent.reactiveProps.editable;
  }

  private async fillCity(): Promise<void> {
    const city = await postCityForZip(this.$apollo, this.delivery);

    if (city) this.delivery.city = city;
  }

  public setDeliveryCountry(country: GQL_allCountries_allCountries) {
    if (!country.code) return;
    this.delivery.country = country;
    this.delivery.countryCode = country.code;
  }

  public unsetDeliveryCountry() {
    this.delivery.country = { code: null };
    this.delivery.countryCode = null;
  }

  private selectedAlternative(item: GQL_customerDeliveryLocations_customer_deliveryLocations): void {
    this.delivery.streetName = item.address;
    this.delivery.additionalStreetName = item.additionalAddress;
    this.delivery.zip = item.zip;
    this.delivery.city = item.city;
    this.delivery.country = item.country;
    this.delivery.countryCode = item.country.code;
  }

  private saveNewDeliveryLocation(): void {
    if (!this.editable || !this.customerId) return;

    this.$apollo.mutate<GQL_updateCustomer_updateCustomer>({
      mutation: UPDATE_CUSTOMER,
      variables: {
        customer: {
          id: this.customerId,
          deliveryLocations: [
            {
              countryCode: this.delivery.country.code,
              address: this.delivery.streetName,
              additionalAddress: this.delivery.additionalStreetName,
              zip: this.delivery.zip,
              city: this.delivery.city,
            },
          ],
        },
      },
    });
    Bugsnag.leaveBreadcrumb('Saving new delivery location', { deliveryLocation: cloneDeep(this.delivery) });
  }
}
